// TerminalPanel_v2.js - Production-Ready Secure Terminal Component
// Enhanced with enterprise-grade security, monitoring, and UX features
import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { 
    Box, Button, Typography, Grid, TextField, CircularProgress, Alert,
    Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle,
    IconButton, Tooltip, Chip, Switch, FormControlLabel, MenuItem,
    Snackbar, Card, CardContent, Collapse, Badge,
    Select, InputLabel, FormControl, Stack, Avatar, AppBar, Toolbar
} from '@mui/material';
import {
    Terminal as TerminalIcon, PlayArrow as PlayArrowIcon, Stop as StopIcon,
    Fullscreen as FullscreenIcon, FullscreenExit as FullscreenExitIcon,
    Settings as SettingsIcon, History as HistoryIcon,
    Security as SecurityIcon, Warning as WarningIcon, Info as InfoIcon,
    Download as DownloadIcon, Shield as ShieldIcon, Timer as TimerIcon, 
    Speed as SpeedIcon, VpnKey as VpnKeyIcon
} from '@mui/icons-material';
import { v4 as uuidv4 } from 'uuid';
import { API_BASE_URL } from './api';
import { Terminal } from '@xterm/xterm';
import { FitAddon } from '@xterm/addon-fit';
import { WebLinksAddon } from '@xterm/addon-web-links';
import { SearchAddon } from '@xterm/addon-search';
import '@xterm/xterm/css/xterm.css';

// Production Constants
const CONFIG = {
    SECURITY: {
        MAX_COMMAND_LENGTH: 8192,
        MAX_SESSION_DURATION: 4 * 60 * 60 * 1000, // 4 hours
        SESSION_WARNING_TIME: 30 * 60 * 1000, // 30 minutes before expiry
        //RATE_LIMIT_COMMANDS: 100, // commands per minute
        HEARTBEAT_INTERVAL: 30000, // 30 seconds
        MAX_FAILED_ATTEMPTS: 3,
        LOCKOUT_DURATION: 5 * 60 * 1000, // 5 minutes
        DANGEROUS_COMMANDS: [
            'rm -rf', 'mkfs', 'dd if=', 'format', ':(){ :|:& };:', 
            'sudo rm', 'shutdown', 'reboot', 'halt', 'poweroff'
        ]
    },
    UI: {
        MAX_STATUS_LOG_ENTRIES: 200,
        TERMINAL_THEMES: {
            dark: {
                name: 'Dark Professional',
                background: '#0d1117',
                foreground: '#e6edf3',
                cursor: '#79c0ff',
                selection: '#264f78',
                black: '#484f58',
                red: '#ff7b72',
                green: '#3fb950',
                yellow: '#d29922',
                blue: '#58a6ff',
                magenta: '#bc8cff',
                cyan: '#39c5cf',
                white: '#e6edf3'
            },
            light: {
                name: 'Light Professional',
                background: '#ffffff',
                foreground: '#24292f',
                cursor: '#0969da',
                selection: '#0969da20'
            },
            matrix: {
                name: 'Matrix Green',
                background: '#000000',
                foreground: '#00ff00',
                cursor: '#00ff00',
                selection: '#00ff0040'
            },
            cyberpunk: {
                name: 'Cyberpunk',
                background: '#0f0f23',
                foreground: '#ff00ff',
                cursor: '#00ffff',
                selection: '#ff00ff40'
            }
        }
    }
};

// Enhanced Utility Functions with better error handling
const encodeBase64 = (str) => {
    try {
        if (!str || typeof str !== 'string') return '';
        // Handle different character encodings properly
        const utf8Bytes = new TextEncoder().encode(str);
        return btoa(String.fromCharCode(...utf8Bytes));
    } catch (e) {
        console.error('Base64 encoding error:', e, 'Input:', str);
        return '';
    }
};

const decodeBase64 = (str) => {
    try {
        if (!str || typeof str !== 'string') return '';
        const bytes = atob(str);
        const utf8Array = new Uint8Array(bytes.length);
        for (let i = 0; i < bytes.length; i++) {
            utf8Array[i] = bytes.charCodeAt(i);
        }
        return new TextDecoder().decode(utf8Array);
    } catch (e) {
        console.error('Base64 decoding error:', e, 'Input:', str);
        return '';
    }
};

const sanitizeInput = (input) => {
    if (!input || typeof input !== 'string') return '';
    // eslint-disable-next-line no-control-regex
    return input.replace(/[\x00-\x08\x0B\x0C\x0E-\x1F\x7F]/g, '');
};

const validateCommand = (command) => {
    if (!command || typeof command !== 'string') return { valid: false, reason: 'Invalid command format' };
    if (command.length > CONFIG.SECURITY.MAX_COMMAND_LENGTH) {
        return { valid: false, reason: 'Command too long' };
    }
    
    const lowerCmd = command.toLowerCase();
    const dangerousCmd = CONFIG.SECURITY.DANGEROUS_COMMANDS.find(dangerous => 
        lowerCmd.includes(dangerous)
    );
    
    if (dangerousCmd) {
        return { valid: false, reason: `Potentially dangerous command detected: ${dangerousCmd}` };
    }
    
    return { valid: true };
};

const formatTime = (ms) => {
    const seconds = Math.floor(ms / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    
    if (hours > 0) return `${hours}h ${minutes % 60}m ${seconds % 60}s`;
    if (minutes > 0) return `${minutes}m ${seconds % 60}s`;
    return `${seconds}s`;
};

export default function TerminalPanel({ apiKey, clientId, clientName, onError, onSessionEnd }) {
    // Core State
    const [sessionId, setSessionId] = useState(null);
    const [correlationId, setCorrelationId] = useState(null);
    const [connecting, setConnecting] = useState(false);
    const [active, setActive] = useState(false);
    const [error, setError] = useState('');
    const [shell, setShell] = useState('bash');
    const [runAsRoot, setRunAsRoot] = useState(false);
    const [statusLog, setStatusLog] = useState([]);

    // Security State
    const [failedAttempts, setFailedAttempts] = useState(0);
    const [lockedUntil, setLockedUntil] = useState(null);
    const [sessionStartTime, setSessionStartTime] = useState(null);
    const [commandCount, setCommandCount] = useState(0);
    const [securityAlerts, setSecurityAlerts] = useState([]);

    // UI State
    const [theme, setTheme] = useState('dark');
    const [fullscreen, setFullscreen] = useState(false);
    const [showSettings, setShowSettings] = useState(false);
    const [showSecurityWarning, setShowSecurityWarning] = useState(false);
    const [showStats, setShowStats] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
    const [terminalSize, setTerminalSize] = useState({ rows: 30, cols: 100 });
    
    // Fullscreen container ref
    const fullscreenContainerRef = useRef();    // Refs
    const terminalRef = useRef();
    const xtermRef = useRef();
    const fitAddonRef = useRef();
    const searchAddonRef = useRef();
    const websocketRef = useRef(null);
    const heartbeatRef = useRef(null);
    const sessionTimeoutRef = useRef(null);
    const commandRateLimitRef = useRef({ count: 0, resetTime: Date.now() + 60000 });
    const securityLogRef = useRef([]);
    // Track current session ID for output
    const currentSessionIdRef = useRef(null);
    // Terminal ready state and output buffer
    const terminalReadyRef = useRef(false);
    const outputBufferRef = useRef([]);

    // Memoized terminal configuration
    const terminalConfig = useMemo(() => ({
        cursorBlink: true,
        rows: terminalSize.rows,
        cols: terminalSize.cols,
        fontFamily: '"Fira Code", "JetBrains Mono", "Cascadia Code", monospace',
        fontSize: 14,
        lineHeight: 1.3,
        letterSpacing: 0.5,
        theme: CONFIG.UI.TERMINAL_THEMES[theme],
        allowTransparency: false,
        bellStyle: 'none',
        scrollback: 10000,
        tabStopWidth: 4,
        rightClickSelectsWord: true,
        macOptionIsMeta: true
    }), [theme, terminalSize]);

    // Enhanced Security Logging
    const logSecurity = useCallback((event, details = {}, level = 'info') => {
        const securityEvent = {
            timestamp: new Date(),
            event,
            details,
            level,
            sessionId: sessionId || 'N/A',
            clientId,
            userAgent: navigator.userAgent
        };
        
        securityLogRef.current.push(securityEvent);
        
        if (level === 'critical' || level === 'warning') {
            setSecurityAlerts(prev => [...prev, securityEvent]);
        }
        
        console.log(`[SECURITY ${level.toUpperCase()}]`, event, details);
    }, [sessionId, clientId]);    // Enhanced Status Logging
    const logStatus = useCallback((msg, isError = false, isSecurityEvent = false) => {
        const timestamp = new Date();
        const logEntry = { 
            msg: sanitizeInput(msg), 
            isError, 
            isSecurityEvent,
            ts: timestamp,
            sessionId: sessionId || 'N/A',
            id: uuidv4()
        };
        
        setStatusLog(prev => {
            const newLog = [...prev, logEntry];
            return newLog.slice(-CONFIG.UI.MAX_STATUS_LOG_ENTRIES);
        });
        
        if (isSecurityEvent) {
            logSecurity(msg, { isError }, isError ? 'warning' : 'info');
        }
        
        if (isError) {
            console.error(`[Terminal Error] ${msg}`);
        } else {
            console.log(`[Terminal] ${msg}`);
        }
    }, [sessionId, logSecurity]);

    // Process buffered output when terminal becomes ready
    const processBufferedOutput = useCallback(() => {
        if (!xtermRef.current || !terminalReadyRef.current) return;
        
        const buffer = outputBufferRef.current;
        if (buffer.length === 0) return;
        
        logStatus(`Processing ${buffer.length} buffered output messages`);
        
        buffer.forEach(outputData => {
            try {
                if (xtermRef.current && typeof xtermRef.current.write === 'function') {
                    xtermRef.current.write(outputData);
                }
            } catch (err) {
                logStatus(`Error writing buffered output: ${err.message}`, true);
            }
        });
        
        // Clear the buffer
        outputBufferRef.current = [];
        logStatus('Buffered output processed and cleared');
    }, [logStatus]);

    // Session Statistics
    const sessionStats = useMemo(() => {
        if (!sessionStartTime) return null;
        
        const duration = Date.now() - sessionStartTime;
        const remaining = CONFIG.SECURITY.MAX_SESSION_DURATION - duration;
        
        return {
            duration: formatTime(duration),
            remaining: formatTime(Math.max(0, remaining)),
            commands: commandCount,
            isNearExpiry: remaining < CONFIG.SECURITY.SESSION_WARNING_TIME
        };
    }, [sessionStartTime, commandCount]);

    // Rate Limiting
    const checkRateLimit = useCallback(() => {
        const now = Date.now();
        const rateLimitData = commandRateLimitRef.current;
        
        if (now > rateLimitData.resetTime) {
            rateLimitData.count = 0;
            rateLimitData.resetTime = now + 60000;
        }
        
        if (rateLimitData.count >= CONFIG.SECURITY.RATE_LIMIT_COMMANDS) {
            logStatus('Rate limit exceeded. Please slow down.', true, true);
            return false;
        }
        
        rateLimitData.count++;
        return true;
    }, [logStatus]);

    // Security Check for Lockout
    const isLocked = useMemo(() => {
        return lockedUntil && Date.now() < lockedUntil;
    }, [lockedUntil]);

    // Heartbeat Management
    const startHeartbeat = useCallback(() => {
        if (heartbeatRef.current) clearInterval(heartbeatRef.current);
        
        heartbeatRef.current = setInterval(() => {
            if (websocketRef.current && websocketRef.current.readyState === WebSocket.OPEN) {
                try {
                    websocketRef.current.send(JSON.stringify({
                        action: 'heartbeat',
                        timestamp: Date.now(),
                        sessionId
                    }));
                } catch (err) {
                    logStatus('Heartbeat failed', true);
                    logSecurity('heartbeat_failed', { error: err.message }, 'warning');
                }
            }
        }, CONFIG.SECURITY.HEARTBEAT_INTERVAL);
    }, [logStatus, logSecurity, sessionId]);

    const stopHeartbeat = useCallback(() => {
        if (heartbeatRef.current) {
            clearInterval(heartbeatRef.current);
            heartbeatRef.current = null;
        }
    }, []);

    // Session Timeout Management
    const startSessionTimeout = useCallback(() => {
        if (sessionTimeoutRef.current) clearTimeout(sessionTimeoutRef.current);
        
        sessionTimeoutRef.current = setTimeout(() => {
            logStatus('Session expired for security', true, true);
            logSecurity('session_timeout', { duration: CONFIG.SECURITY.MAX_SESSION_DURATION }, 'warning');
            setSnackbar({
                open: true,
                message: 'Session expired for security. Please reconnect.',
                severity: 'warning'
            });
            closeSession();
        }, CONFIG.SECURITY.MAX_SESSION_DURATION);
    }, [logStatus, logSecurity]);

    const stopSessionTimeout = useCallback(() => {
        if (sessionTimeoutRef.current) {
            clearTimeout(sessionTimeoutRef.current);
            sessionTimeoutRef.current = null;
        }
    }, []);

    // Add a ref to prevent duplicate cleanup
    const cleanedUpRef = useRef(false);

    // Enhanced Connection Cleanup
    const cleanupConnection = useCallback(() => {
        if (cleanedUpRef.current) return;
        cleanedUpRef.current = true;
        logStatus('Cleaning up connection...', false, true);
        stopHeartbeat();
        stopSessionTimeout();
        if (websocketRef.current) {
            try {
                websocketRef.current.onopen = null;
                websocketRef.current.onmessage = null;
                websocketRef.current.onerror = null;
                websocketRef.current.onclose = null;
                if (websocketRef.current.readyState === WebSocket.OPEN || 
                    websocketRef.current.readyState === WebSocket.CONNECTING) {
                    websocketRef.current.close(1000, 'Clean disconnect');
                }
                logSecurity('websocket_disconnected', { clean: true }, 'info');
            } catch (err) {
                logStatus(`Error during cleanup: ${err.message}`, true, true);
                logSecurity('cleanup_error', { error: err.message }, 'warning');
            } finally {
                websocketRef.current = null;
            }
        }        setConnecting(false);
        setActive(false);
        setSessionId(null);
        setCorrelationId(null);
        setSessionStartTime(null);
        setCommandCount(0);
        commandRateLimitRef.current = { count: 0, resetTime: Date.now() + 60000 };
        // Reset terminal state
        terminalReadyRef.current = false;
        outputBufferRef.current = [];
    }, [logStatus, logSecurity, stopHeartbeat, stopSessionTimeout]);

    // Reset cleanedUpRef on session start
    useEffect(() => {
        cleanedUpRef.current = false;
    }, [sessionId]);

    // Enhanced Session Start - Fix the closure issue
    const startSession = useCallback(async () => {
        // Security checks
        if (isLocked) {
            const remainingTime = Math.ceil((lockedUntil - Date.now()) / 1000);
            setError(`Account locked due to failed attempts. Try again in ${remainingTime} seconds.`);
            return;
        }

        if (!apiKey || !clientId) {
            setFailedAttempts(prev => prev + 1);
            if (failedAttempts >= CONFIG.SECURITY.MAX_FAILED_ATTEMPTS - 1) {
                setLockedUntil(Date.now() + CONFIG.SECURITY.LOCKOUT_DURATION);
                logSecurity('account_locked', { attempts: failedAttempts + 1 }, 'critical');
            }
            logStatus('Missing authentication parameters', true, true);
            setError('Authentication required');
            return;
        }

        if (runAsRoot && !showSecurityWarning) {
            setShowSecurityWarning(true);
            return;
        }

        logSecurity('session_start_attempt', { runAsRoot, shell }, 'info');
        
        setConnecting(true);
        setError('');
        setSessionId(null);
        setCorrelationId(null);
        setStatusLog([]);
        setSessionStartTime(Date.now());
        setCommandCount(0);
        
        cleanupConnection();
        
        if (xtermRef.current) {
            try {
                xtermRef.current.dispose();
                xtermRef.current = null;
            } catch (err) {
                console.warn('Error disposing terminal:', err);
            }
        }

        try {
            logStatus('Initiating secure connection...', false, true);
            
            const sanitizedShell = sanitizeInput(shell);
            if (shell && shell !== sanitizedShell) {
                logSecurity('shell_input_sanitized', { original: shell, sanitized: sanitizedShell }, 'warning');
            }

            // Enhanced authentication - simplified approach like working HTML
            console.log('DEBUG: Starting WS ticket request...');
            console.log('DEBUG: API_BASE_URL:', API_BASE_URL);
            console.log('DEBUG: apiKey present:', !!apiKey);
            console.log('DEBUG: clientId:', clientId);
            
            const ticketResp = await fetch(`${API_BASE_URL}/api/auth/ws-ticket`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${apiKey}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({})
            });

            console.log('DEBUG: Ticket response status:', ticketResp.status);

            if (!ticketResp.ok) {
                const errorText = await ticketResp.text();
                console.error('DEBUG: Ticket request failed:', errorText);
                setFailedAttempts(prev => prev + 1);
                throw new Error(`Authentication failed: ${ticketResp.status} - ${errorText}`);
            }

            const ticketData = await ticketResp.json();
            if (!ticketData.ticket || typeof ticketData.ticket !== 'string') {
                throw new Error('Invalid authentication response');
            }

            logSecurity('authentication_success', { elevated: runAsRoot }, 'info');
            setFailedAttempts(0); // Reset on success

            // Build secure WebSocket URL
            let wsScheme = API_BASE_URL.startsWith('https') ? 'wss' : 'ws';
            let wsHost = API_BASE_URL.split('//')[1];
            if (wsHost.includes('/')) wsHost = wsHost.substring(0, wsHost.indexOf('/'));

            const wsUrl = `${wsScheme}://${wsHost}/ws/admin?ticket=${encodeURIComponent(ticketData.ticket)}`;
            logStatus('Establishing secure WebSocket connection...');
            logSecurity('websocket_connecting', { url: wsUrl.replace(/ticket=[^&]+/, 'ticket=[REDACTED]') }, 'info');

            const wsConn = new WebSocket(wsUrl);
            websocketRef.current = wsConn;
            
            // Set connection timeout
            const connectionTimeout = setTimeout(() => {
                if (wsConn.readyState === WebSocket.CONNECTING) {
                    logStatus('WebSocket connection timeout', true);
                    logSecurity('websocket_timeout', {}, 'warning');
                    wsConn.close();
                    setConnecting(false);
                    setError('Connection timeout - please try again');
                }
            }, 15000); // 15 second timeout
            
            const corrId = uuidv4();
            setCorrelationId(corrId);
            // Use local variable exactly like backup - this is the key fix
            let currentSessionID = null;

            // Enhanced WebSocket handlers
            wsConn.onopen = () => {
                clearTimeout(connectionTimeout);
                logStatus('Secure connection established', false, true);
                logSecurity('websocket_connected', { url: wsUrl.replace(/ticket=[^&]+/, 'ticket=[REDACTED]') }, 'info');
                
                setConnecting(false);
                setActive(true);
                startHeartbeat();
                startSessionTimeout();
                
                const sessionRequest = {
                    action: 'admin_trigger_terminal_session',
                    payload: {
                        targetClientID: sanitizeInput(clientId),
                        correlationID: corrId,
                        shell: sanitizedShell || '',
                        runAsRoot,
                        securityLevel: runAsRoot ? 'elevated' : 'standard',
                        timestamp: Date.now(),
                        terminalConfig: {
                            rows: terminalSize.rows,
                            cols: terminalSize.cols
                        }
                    }
                };
                
                try {
                    wsConn.send(JSON.stringify(sessionRequest));
                    logSecurity('session_request_sent', sessionRequest.payload, 'info');
                    logStatus('Terminal session request sent successfully');
                } catch (err) {
                    logStatus(`Failed to send session request: ${err.message}`, true, true);
                    logSecurity('session_request_failed', { error: err.message }, 'warning');
                    cleanupConnection();
                }
            };

            // Copy exact backup logic for message handling
            wsConn.onmessage = (event) => {
                try {
                    const msg = JSON.parse(event.data);
                    logStatus(`Received message: ${msg.type}`);
                    
                    if (msg.type === 'admin_event_terminal_session_ready') {
                        if (msg.payload.correlationID === corrId) {
                            if (msg.payload.error) {
                                setError(msg.payload.error);
                                logStatus(`Terminal session failed: ${msg.payload.error}`, true);
                                logSecurity('session_failed', { error: msg.payload.error }, 'warning');
                                setActive(false);
                                wsConn.close();
                            } else {
                                currentSessionID = msg.payload.sessionID; // Set local variable immediately like backup
                                setSessionId(msg.payload.sessionID);
                                logStatus(`Terminal session ${msg.payload.sessionID} ready. Initializing xterm.js.`);
                                logSecurity('session_established', { sessionId: msg.payload.sessionID }, 'info');
                                
                                // Initialize terminal exactly like backup
                                initializeXterm(wsConn, msg.payload.sessionID);
                            }
                        }
                    } else if (msg.type === 'admin_event_terminal_output_from_client') {
                        // Exact backup logic for output handling
                        if (msg.payload.sessionID === currentSessionID) {
                            if (xtermRef.current && terminalReadyRef.current) {
                                try {
                                    if (!msg.payload.data && msg.payload.data !== '') {
                                        logStatus('Received null terminal output data', true);
                                        return;
                                    }
                                    
                                    const decoded = decodeBase64(msg.payload.data);
                                    if (decoded !== null && decoded !== undefined) {
                                        xtermRef.current.write(decoded);
                                        logSecurity('output_received', { length: decoded.length }, 'info');
                                    } else {
                                        logStatus('Failed to decode terminal output', true);
                                        logSecurity('decode_failed', { 
                                            dataLength: msg.payload.data ? msg.payload.data.length : 0,
                                            sessionId: msg.payload.sessionID 
                                        }, 'warning');
                                    }
                                } catch (e) {
                                    logStatus(`Error decoding terminal data: ${e}`, true);
                                    logSecurity('output_processing_error', { 
                                        error: e.message,
                                        sessionId: msg.payload.sessionID 
                                    }, 'warning');
                                    console.error('Base64 decode error:', e, 'Data:', msg.payload.data);
                                }
                            } else {
                                // Buffer output if terminal is not ready yet
                                if (!msg.payload.data && msg.payload.data !== '') return;
                                
                                const decoded = decodeBase64(msg.payload.data);
                                if (decoded !== null && decoded !== undefined) {
                                    outputBufferRef.current.push(decoded);
                                    logStatus(`Buffering output (${outputBufferRef.current.length} messages) - terminal not ready`);
                                }
                            }
                        } else {
                            logStatus(`Ignoring output - session mismatch: ${msg.payload.sessionID} vs ${currentSessionID}`);
                        }
                    } else if (msg.type === 'admin_event_terminal_session_ended') {
                        logStatus(`Session ended: ${msg.payload.reason || 'Unknown'}`, true);
                        logSecurity('session_ended', { reason: msg.payload.reason }, 'info');
                        if (xtermRef.current) {
                            xtermRef.current.writeln('\r\n\x1b[91m--- SESSION ENDED ---\x1b[0m');
                        }
                        currentSessionID = null;
                        setActive(false);
                        setSessionId(null);
                        wsConn.close();
                        if (onSessionEnd) onSessionEnd();
                    } else if (msg.type === 'admin_event_terminal_error') {
                        setError(msg.payload.error);
                        logStatus(`Terminal error: ${msg.payload.error}`, true);
                        logSecurity('terminal_error', { error: msg.payload.error }, 'warning');
                        setActive(false);
                        wsConn.close();
                    } else if (msg.type === 'heartbeat') {
                        // Heartbeat acknowledgment - no action needed
                    } else {
                        logStatus(`Unknown message type: ${msg.type}`);
                        logSecurity('unknown_message', { type: msg.type, payload: msg.payload }, 'warning');
                    }
                } catch (e) {
                    logStatus(`Failed to parse message: ${e.message}`, true);
                    logSecurity('message_parse_error', { error: e.message, data: event.data }, 'warning');
                }
            };

            wsConn.onerror = (err) => {
                clearTimeout(connectionTimeout);
                setError('Connection error occurred');
                logStatus('WebSocket error', true);
                logSecurity('websocket_error', { error: err }, 'warning');
                setConnecting(false);
                setActive(false);
                cleanupConnection();
                if (onError) onError('WebSocket connection error');
            };

            wsConn.onclose = (event) => {
                clearTimeout(connectionTimeout);
                logStatus(`Connection closed: ${event.code} - ${event.reason || 'No reason'}`);
                logSecurity('websocket_closed', { code: event.code, reason: event.reason }, 'info');
                setActive(false);
                setConnecting(false);
                websocketRef.current = null;
            };

        } catch (err) {
            setError(`Failed to start session: ${err.message}`);
            logStatus(`Session start failed: ${err.message}`, true);
            logSecurity('session_start_failed', { error: err.message }, 'warning');
            setConnecting(false);
            if (onError) onError(`Failed to start session: ${err.message}`);
        }
    }, [apiKey, clientId, runAsRoot, showSecurityWarning, shell, isLocked, lockedUntil, failedAttempts, 
        logStatus, logSecurity, cleanupConnection, startHeartbeat, startSessionTimeout, terminalSize, onError, onSessionEnd]);

    // Initialize xterm exactly like backup - fix dimensions error
    const initializeXterm = useCallback((wsConn, sessionID) => {
        // Reset terminal ready state
        terminalReadyRef.current = false;
        outputBufferRef.current = [];
        
        if (xtermRef.current) {
            try {
                xtermRef.current.dispose();
            } catch (err) {
                console.warn('Error disposing previous terminal:', err);
            }
            xtermRef.current = null;
        }
        
        if (!terminalRef.current) {
            logStatus('Terminal container not available', true);
            return;
        }

        // Clear container and ensure proper dimensions BEFORE creating terminal
        terminalRef.current.innerHTML = '';
        
        // Force container to have dimensions before terminal creation - exactly like backup
        terminalRef.current.style.width = '100%';
        terminalRef.current.style.height = '420px';
        terminalRef.current.style.minHeight = '320px';
        terminalRef.current.style.maxHeight = '600px';
        terminalRef.current.style.background = '#181a1b';
        terminalRef.current.style.borderRadius = '8px';
        terminalRef.current.style.border = '1px solid #23272e';
        terminalRef.current.style.overflow = 'hidden';
        terminalRef.current.style.display = 'block';
        
        // Wait a moment for the container to stabilize its dimensions
        setTimeout(() => {
            try {
                // Create terminal exactly like backup with fixed dimensions
                const term = new Terminal({
                    cursorBlink: true,
                    rows: 24,
                    cols: 80,
                    fontFamily: 'Fira Mono, monospace',
                    fontSize: 15,
                    theme: {
                        background: '#181a1b',
                        foreground: '#eaeaea',
                        cursor: '#eaeaea',
                        selection: '#44475a',
                    }
                });
                
                const fitAddon = new FitAddon();
                term.loadAddon(fitAddon);
                
                // Open terminal first - this creates the DOM structure
                // Use the correct container based on fullscreen mode
                const terminalContainer = terminalRef.current;
                if (!terminalContainer) {
                    logStatus('Terminal container not found', true);
                    return;
                }
                
                term.open(terminalContainer);
                
                // Wait for DOM to stabilize before fitting
                setTimeout(() => {
                    try {
                        // Only fit if container has proper dimensions
                        if (terminalRef.current && terminalRef.current.offsetWidth > 0 && terminalRef.current.offsetHeight > 0) {
                            fitAddon.fit();
                            logStatus('Terminal fitted successfully');
                        } else {
                            logStatus('Terminal container not ready for fitting, using default size');
                        }
                    } catch (fitError) {
                        logStatus(`Terminal fit error: ${fitError.message}`, true);
                        console.warn('Fit error, continuing with default size:', fitError);
                    }
                }, 100);
                
                // Set refs immediately like backup
                xtermRef.current = term;
                fitAddonRef.current = fitAddon;

                logStatus('Terminal opened');

                // Focus handling exactly like backup
                setTimeout(() => {
                    term.focus();
                    const textarea = terminalRef.current.querySelector('textarea');
                    if (textarea) {
                        textarea.focus();
                        textarea.style.pointerEvents = 'auto';
                        logStatus('Terminal focused successfully');
                    } else {
                        logStatus('Terminal textarea not found for focus');
                    }
                }, 300);

                // Input handler exactly like backup
                const inputHandler = (data) => {
                    if (!data || typeof data !== 'string') {
                        logStatus('Invalid input data received', true);
                        return;
                    }

                    // Keep rate limiting for security
                    if (!checkRateLimit()) {
                        logStatus('Input rate limit exceeded', true);
                        return;
                    }

                    if (wsConn && wsConn.readyState === WebSocket.OPEN && sessionID) {
                        try {
                            setCommandCount(prev => prev + 1);
                            
                            const encodedData = encodeBase64(data);
                            if (!encodedData && data.length > 0) {
                                logStatus('Failed to encode input data', true);
                                return;
                            }
                            
                            wsConn.send(JSON.stringify({
                                action: 'admin_terminal_input_to_client',
                                payload: {
                                    sessionID,
                                    data: encodedData
                                }
                            }));
                            
                            logSecurity('input_sent', { length: data.length }, 'info');
                        } catch (err) {
                            logStatus(`Failed to send input: ${err.message}`, true);
                            logSecurity('input_send_error', { error: err.message }, 'warning');
                        }
                    } else {
                        const wsState = wsConn ? wsConn.readyState : 'null';
                        logStatus(`Cannot send input - WS state: ${wsState}, SessionID: ${sessionID}`, true);
                    }
                };
                
                term.onData(inputHandler);
                
                // Resize handler with safe fit
                const resizeHandler = (size) => {
                    if (wsConn && wsConn.readyState === WebSocket.OPEN && sessionID) {
                        try {
                            logStatus(`Terminal resized to ${size.cols}x${size.rows}`);
                            wsConn.send(JSON.stringify({
                                action: 'admin_resize_terminal_session',
                                payload: {
                                    sessionID,
                                    rows: size.rows,
                                    cols: size.cols
                                }
                            }));
                            setTerminalSize(size);
                        } catch (err) {
                            logStatus(`Resize handler error: ${err.message}`, true);
                        }
                    }
                };
                
                term.onResize(resizeHandler);

                // Safe window resize handling
                const handleResize = () => {
                    try {
                        if (fitAddon && terminalRef.current && terminalRef.current.offsetWidth > 0) {
                            fitAddon.fit();
                        }
                    } catch (err) {
                        console.warn('Window resize fit error:', err);
                    }
                };
                window.addEventListener('resize', handleResize);
                
                // Store the cleanup function for later use
                term._resizeCleanup = () => {
                    window.removeEventListener('resize', handleResize);
                };
                
                logStatus('xterm.js initialized and focused. Ready for input.');
                
                // Mark terminal as ready and process any buffered output
                terminalReadyRef.current = true;
                
                // Process buffered output after a short delay
                setTimeout(() => {
                    processBufferedOutput();
                }, 100);

            } catch (initError) {
                logStatus(`Terminal initialization error: ${initError.message}`, true);
                logSecurity('terminal_init_error', { error: initError.message }, 'warning');
                console.error('Terminal init error:', initError);
                return;
            }
        }, 50); // Small delay to ensure container dimensions are stable
    }, [logStatus, logSecurity, checkRateLimit, processBufferedOutput]);

    // Enhanced Close Session
    const closeSession = useCallback(() => {
        // Prevent duplicate cleanup/logs
        if (!active && !sessionId && !xtermRef.current) return;
        logStatus('Closing session...', false, true);
        logSecurity('session_close_initiated', { sessionId }, 'info');
        
        if (xtermRef.current) {
            try {
                xtermRef.current.writeln('\r\n\x1b[93m--- CLOSING SESSION ---\x1b[0m');
            } catch (err) {
                console.warn('Error writing close message:', err);
            }
        }
        
        if (websocketRef.current && websocketRef.current.readyState === WebSocket.OPEN && sessionId) {
            try {
                websocketRef.current.send(JSON.stringify({
                    action: 'admin_close_terminal_session',
                    payload: { sessionID: sessionId }
                }));
            } catch (err) {
                console.warn('Error sending close message:', err);
            }
        }
        
        setActive(false);
        setSessionId(null);
        setCorrelationId(null);
        setError('Session closed');
        setFullscreen(false); // Reset fullscreen state when session closes
        
        cleanupConnection();
        
        // Robust terminal cleanup
        if (xtermRef.current) {
            try {
                if (typeof xtermRef.current._cleanup === 'function') {
                    try {
                        xtermRef.current._cleanup();
                    } catch (cleanupErr) {
                        console.warn('Error in terminal _cleanup:', cleanupErr);
                    }
                }
                xtermRef.current.dispose();
            } catch (err) {
                console.warn('Error disposing terminal:', err);
            } finally {
                xtermRef.current = null;
            }
        }
        
        logStatus('Session closed successfully');
        logSecurity('session_closed', { duration: sessionStats?.duration }, 'info');
        
        if (onSessionEnd) onSessionEnd();
    }, [active, sessionId, sessionStats, logStatus, logSecurity, cleanupConnection, onSessionEnd]);

    // Test terminal functionality
    const testTerminalConnection = useCallback(() => {
        if (!xtermRef.current || !websocketRef.current || !sessionId) {
            logStatus('Terminal not ready for testing', true);
            return;
        }

        logStatus('Testing terminal connection...');
        
        // Test 1: Send a simple echo command
        try {
            const testInput = 'echo "Terminal test: $(date)"\n';
            const encodedTest = encodeBase64(testInput);
            
            if (!encodedTest) {
                logStatus('Failed to encode test input', true);
                return;
            }
            
            websocketRef.current.send(JSON.stringify({
                action: 'admin_terminal_input_to_client',
                payload: {
                    sessionID: sessionId,
                    data: encodedTest
                }
            }));
            
            logStatus('Test command sent successfully');
            logSecurity('test_command_sent', { command: 'echo test' }, 'info');
            
        } catch (err) {
            logStatus(`Test failed: ${err.message}`, true);
            logSecurity('test_failed', { error: err.message }, 'warning');
        }
    }, [sessionId, logStatus, logSecurity]);

    // Utility Functions
    const showSnackbar = useCallback((message, severity = 'info') => {
        setSnackbar({ open: true, message, severity });
    }, []);

    const exportLogs = useCallback(() => {
        const logs = statusLog.map(entry => 
            `[${entry.ts.toISOString()}] ${entry.isError ? 'ERROR' : 'INFO'}${entry.isSecurityEvent ? ' (SECURITY)' : ''}: ${entry.msg}`
        ).join('\n');
        
        const securityLogs = securityLogRef.current.map(entry =>
            `[${entry.timestamp.toISOString()}] SECURITY ${entry.level.toUpperCase()}: ${entry.event} - ${JSON.stringify(entry.details)}`
        ).join('\n');
        
        const fullLogs = `TERMINAL LOGS\n${'='.repeat(50)}\n${logs}\n\nSECURITY LOGS\n${'='.repeat(50)}\n${securityLogs}`;
        
        const blob = new Blob([fullLogs], { type: 'text/plain' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `terminal-logs-${clientId}-${new Date().toISOString().split('T')[0]}.txt`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        
        showSnackbar('Logs exported successfully', 'success');
        logSecurity('logs_exported', { entries: statusLog.length }, 'info');
    }, [statusLog, clientId, showSnackbar, logSecurity]);

    // Effects
    useEffect(() => {
        const el = document.getElementById('status-log');
        if (el) el.scrollTop = el.scrollHeight;
    }, [statusLog]);

    useEffect(() => {
        return () => {
            logSecurity('component_unmounting', {}, 'info');
            stopHeartbeat();
            stopSessionTimeout();
            cleanupConnection();
            
            if (xtermRef.current) {
                try {
                    if (xtermRef.current._cleanup) {
                        try {
                            xtermRef.current._cleanup();
                        } catch (cleanupErr) {
                            console.warn('Error in terminal _cleanup:', cleanupErr);
                        }
                    }
                    xtermRef.current.dispose();
                } catch (err) {
                    console.warn('Error during unmount cleanup:', err);
                } finally {
                    xtermRef.current = null;
                }
            }
        };
    }, [logSecurity, stopHeartbeat, stopSessionTimeout, cleanupConnection]);    // Auto-resize handling
    useEffect(() => {
        let resizeTimeout = null;
        
        const handleResize = () => {
            if (resizeTimeout) {
                clearTimeout(resizeTimeout);
            }
            resizeTimeout = setTimeout(() => {
                if (fitAddonRef.current && xtermRef.current) {
                    try {
                        if (terminalRef.current && terminalRef.current.offsetWidth > 0) {
                            fitAddonRef.current.fit();
                            logStatus('Terminal resized and fitted');
                        }
                    } catch (error) {
                        console.error('Terminal resize error:', error);
                        logStatus(`Terminal resize error: ${error.message}`, true);
                    }
                }
            }, 150);
        };
        window.addEventListener('resize', handleResize);
        setTimeout(handleResize, 300);
        return () => {
            window.removeEventListener('resize', handleResize);
            if (resizeTimeout) {
                clearTimeout(resizeTimeout);
            }
        };
    }, []);// Terminal focus handling
    useEffect(() => {
        const el = terminalRef.current;
        if (!el) return;
        
        const focusHandler = (e) => {
            try {
                if (xtermRef.current && typeof xtermRef.current.focus === 'function' && !xtermRef.current._textarea?.disabled) {
                    e.preventDefault();
                    e.stopPropagation();
                    xtermRef.current.focus();
                    console.log('Terminal focused via click');
                }
            } catch (err) {
                console.error('Focus error:', err);
            }
        };
        
        el.addEventListener('click', focusHandler);
        
        if (active && xtermRef.current && typeof xtermRef.current.focus === 'function') {
            setTimeout(() => {
                try {
                    if (xtermRef.current && typeof xtermRef.current.focus === 'function') xtermRef.current.focus();
                    console.log('Terminal auto-focused');
                } catch (err) {
                    console.error('Initial focus error:', err);
                }
            }, 100);
        }
        
        return () => {
            el.removeEventListener('click', focusHandler);
        };
    }, [active, sessionId]);

    // Security Dialogs
    const SecurityWarningDialog = () => (
        <Dialog open={showSecurityWarning} onClose={() => setShowSecurityWarning(false)}>
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <WarningIcon color="warning" />
                Security Warning
            </DialogTitle>
            <DialogContent>
                <DialogContentText>
                    You are about to start a terminal session with root privileges. This provides 
                    full system access and should only be used when necessary. All commands will be logged.
                </DialogContentText>
            </DialogContent>
            <DialogActions>
                <Button onClick={() => setShowSecurityWarning(false)}>Cancel</Button>
                <Button 
                    onClick={() => {
                        setShowSecurityWarning(false);
                        startSession();
                    }} 
                    variant="contained" 
                    color="warning"
                >
                    I Understand, Continue
                </Button>
            </DialogActions>
        </Dialog>
    );

    // Settings Dialog
    const SettingsDialog = () => (
        <Dialog open={showSettings} onClose={() => setShowSettings(false)} maxWidth="sm" fullWidth>
            <DialogTitle>Terminal Settings</DialogTitle>
            <DialogContent>
                <Stack spacing={3} sx={{ mt: 1 }}>
                    <FormControl fullWidth>
                        <InputLabel>Theme</InputLabel>
                        <Select
                            value={theme}
                            onChange={(e) => setTheme(e.target.value)}
                            label="Theme"
                        >
                            {Object.entries(CONFIG.UI.TERMINAL_THEMES).map(([key, config]) => (
                                <MenuItem key={key} value={key}>{config.name}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    
                    <Grid container spacing={2}>
                        <Grid item xs={6}>
                            <TextField
                                label="Rows"
                                type="number"
                                value={terminalSize.rows}
                                onChange={(e) => setTerminalSize(prev => ({ ...prev, rows: parseInt(e.target.value) || 24 }))}
                                fullWidth
                                inputProps={{ min: 10, max: 50 }}
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField
                                label="Columns"
                                type="number"
                                value={terminalSize.cols}
                                onChange={(e) => setTerminalSize(prev => ({ ...prev, cols: parseInt(e.target.value) || 80 }))}
                                fullWidth
                                inputProps={{ min: 40, max: 200 }}
                            />
                        </Grid>
                    </Grid>
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={() => setShowSettings(false)}>Close</Button>
            </DialogActions>
        </Dialog>
    );

    // Fullscreen functionality and keyboard handlers
    const toggleFullscreen = useCallback(() => {
        setFullscreen(prev => {
            const newFullscreen = !prev;
            if (newFullscreen) {
                logStatus('Entering fullscreen mode');
            } else {
                // When exiting fullscreen, close the session
                logStatus('Exiting fullscreen mode - closing session');
                closeSession();
                return false; // Return false since session will be closed
            }
            return newFullscreen;
        });
    }, [logStatus, closeSession]);

    // Keyboard handlers for fullscreen
    useEffect(() => {
        const handleKeyDown = (event) => {
            // ESC key to exit fullscreen and close session
            if (event.key === 'Escape' && fullscreen) {
                event.preventDefault();
                event.stopPropagation();
                logStatus('Exited fullscreen mode with ESC key - closing session');
                closeSession();
            }
            
            // F11 key to toggle fullscreen (common browser shortcut)
            if (event.key === 'F11') {
                event.preventDefault();
                event.stopPropagation();
                toggleFullscreen();
            }
        };

        // Only add listener when fullscreen is active or F11 should work
        document.addEventListener('keydown', handleKeyDown, true);
        
        return () => {
            document.removeEventListener('keydown', handleKeyDown, true);
        };
    }, [fullscreen, toggleFullscreen, logStatus, closeSession]);

    // Handle fullscreen container mounting/unmounting
    useEffect(() => {
        if (fullscreen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'auto';
        }
        
        return () => {
            document.body.style.overflow = 'auto';
        };
    }, [fullscreen]);

    // Handle fullscreen mode changes - only for entering fullscreen
    const handleFullscreenResize = useCallback(() => {
        if (!xtermRef.current || !fitAddonRef.current || !active || !fullscreen) return;
        
        try {
            // Wait a bit for DOM updates to complete
            setTimeout(() => {
                const fullscreenContainer = document.getElementById('terminal-fullscreen-container');
                
                if (!fullscreenContainer) {
                    logStatus('Fullscreen container not found when entering fullscreen', true);
                    return;
                }
                
                // Look for terminal element in the DOM
                let terminalElement = document.querySelector('.xterm');
                
                if (terminalElement) {
                    const currentContainer = terminalElement.parentElement;
                    
                    logStatus(`Moving terminal from ${currentContainer?.id || 'unknown'} to ${fullscreenContainer.id}`);
                    
                    if (currentContainer !== fullscreenContainer) {
                        // Move the terminal element to the fullscreen container
                        try {
                            fullscreenContainer.appendChild(terminalElement);
                            logStatus('Terminal successfully moved to fullscreen container');
                        } catch (moveErr) {
                            logStatus(`Failed to move terminal: ${moveErr.message}`, true);
                            // Fallback: reattach terminal
                            fullscreenContainer.innerHTML = '';
                            xtermRef.current.open(fullscreenContainer);
                            logStatus('Terminal reattached as fallback');
                        }
                    } else {
                        logStatus('Terminal already in fullscreen container');
                    }
                } else {
                    logStatus('Terminal element not found, reattaching to fullscreen container');
                    // Terminal element missing, reattach it
                    fullscreenContainer.innerHTML = '';
                    xtermRef.current.open(fullscreenContainer);
                    logStatus('Terminal reattached to fullscreen container');
                }
                
                // Resize and focus after a short delay
                setTimeout(() => {
                    try {
                        if (fitAddonRef.current && xtermRef.current) {
                            fitAddonRef.current.fit();
                            xtermRef.current.focus();
                            logStatus('Terminal resized and focused in fullscreen mode');
                        }
                    } catch (resizeErr) {
                        logStatus(`Terminal resize error: ${resizeErr.message}`, true);
                    }
                }, 100);
            }, 50); // Wait for DOM updates
        } catch (err) {
            logStatus(`Terminal fullscreen resize error: ${err.message}`, true);
        }
    }, [logStatus, active, fullscreen]);

    // Handle fullscreen mode changes - only trigger when entering fullscreen
    useEffect(() => {
        if (xtermRef.current && active && fullscreen) {
            // Delay to ensure DOM updates complete before moving terminal
            const timer = setTimeout(() => {
                handleFullscreenResize();
            }, 100);
            
            return () => clearTimeout(timer);
        }
    }, [fullscreen, handleFullscreenResize, active]);

    return (
        <>
            {/* Fullscreen Overlay */}
            {fullscreen && (
                <Box
                    ref={fullscreenContainerRef}
                    sx={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        width: '100vw',
                        height: '100vh',
                        zIndex: 9999,
                        backgroundColor: '#0c0c0c',
                        background: 'linear-gradient(135deg, #0c0c0c 0%, #1a1a1a 100%)',
                        display: 'flex',
                        flexDirection: 'column',
                        overflow: 'hidden'
                    }}
                >
                    {/* Fullscreen Header */}
                    <AppBar position="static" elevation={0} sx={{ backgroundColor: 'rgba(0,0,0,0.9)', backdropFilter: 'blur(10px)' }}>
                        <Toolbar variant="dense">
                            <Avatar sx={{ bgcolor: 'primary.dark', mr: 2, width: 32, height: 32 }}>
                                <TerminalIcon fontSize="small" />
                            </Avatar>
                            <Box sx={{ flexGrow: 1 }}>
                                <Typography variant="h6" sx={{ fontSize: '1rem' }}>Secure Remote Terminal - Fullscreen</Typography>
                                <Typography variant="caption" sx={{ opacity: 0.8, fontSize: '0.7rem' }}>
                                    {clientName ? `Connected to ${clientName}` : `Client: ${clientId}`} | Press ESC to exit fullscreen
                                </Typography>
                            </Box>
                            
                            {/* Compact Session Status */}
                            {active && sessionStats && (
                                <Stack direction="row" spacing={1} sx={{ mr: 2 }}>
                                    <Chip 
                                        icon={<TimerIcon />} 
                                        label={sessionStats.duration}
                                        size="small"
                                        color={sessionStats.isNearExpiry ? "warning" : "success"}
                                        sx={{ height: 24 }}
                                    />
                                    <Chip 
                                        icon={<SpeedIcon />} 
                                        label={`${sessionStats.commands} cmds`}
                                        size="small"
                                        color="info"
                                        sx={{ height: 24 }}
                                    />
                                </Stack>
                            )}
                            
                            {/* Compact Action Buttons */}
                            <IconButton 
                                color="inherit" 
                                onClick={() => setShowSettings(true)}
                                size="small"
                                sx={{ mr: 1 }}
                            >
                                <SettingsIcon fontSize="small" />
                            </IconButton>
                            <IconButton 
                                color="inherit" 
                                onClick={toggleFullscreen}
                                size="small"
                            >
                                <FullscreenExitIcon fontSize="small" />
                            </IconButton>
                        </Toolbar>
                    </AppBar>

                    {/* Fullscreen Terminal Container - Use a separate ref */}
                    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                        <Box
                            id="terminal-fullscreen-container"
                            sx={{
                                flex: 1,
                                width: '100%',
                                backgroundColor: CONFIG.UI.TERMINAL_THEMES[theme].background,
                                cursor: 'text',
                                '&:focus-within': {
                                    outline: '2px solid',
                                    outlineColor: 'primary.main',
                                    outlineOffset: '-2px'
                                }
                            }}
                            onClick={() => {
                                try {
                                    if (xtermRef.current) {
                                        xtermRef.current.focus();
                                    }
                                } catch (err) {
                                    console.error('Focus error:', err);
                                }
                            }}
                        />
                    </Box>

                    {/* Fullscreen Quick Controls */}
                    <Box sx={{ 
                        backgroundColor: 'rgba(0,0,0,0.9)', 
                        backdropFilter: 'blur(10px)',
                        p: 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between'
                    }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {active ? (
                                <Button
                                    variant="contained"
                                    color="error"
                                    onClick={closeSession}
                                    startIcon={<StopIcon />}
                                    size="small"
                                >
                                    End Session
                                </Button>
                            ) : (
                                <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={startSession}
                                    disabled={connecting || isLocked}
                                    startIcon={connecting ? <CircularProgress size={16} /> : <PlayArrowIcon />}
                                    size="small"
                                >
                                    {connecting ? 'Connecting...' : 'Start Terminal'}
                                </Button>
                            )}
                        </Box>
                        
                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                            Terminal Size: {terminalSize.cols}×{terminalSize.rows}
                        </Typography>
                    </Box>
                </Box>
            )}

            {/* Normal Mode Container */}
            <Box sx={{ 
                p: fullscreen ? 0 : 3, 
                bgcolor: fullscreen ? 'transparent' : 'background.default', 
                minHeight: fullscreen ? 0 : '100vh',
                visibility: fullscreen ? 'hidden' : 'visible',
                position: fullscreen ? 'absolute' : 'static',
                left: fullscreen ? '-9999px' : 'auto',
                top: fullscreen ? '-9999px' : 'auto'
            }}>
                {/* Header */}
                <Card elevation={3} sx={{ mb: 3, overflow: 'visible' }}>
                    <AppBar position="static" elevation={0} sx={{ borderRadius: '4px 4px 0 0' }}>
                        <Toolbar>
                            <Avatar sx={{ bgcolor: 'primary.dark', mr: 2 }}>
                                <TerminalIcon />
                            </Avatar>
                            <Box sx={{ flexGrow: 1 }}>
                                <Typography variant="h6">Secure Remote Terminal</Typography>
                                <Typography variant="caption" sx={{ opacity: 0.8 }}>
                                    {clientName ? `Connected to ${clientName}` : `Client: ${clientId}`}
                                </Typography>
                            </Box>
                            
                            {/* Session Status Indicators */}
                            {active && sessionStats && (
                                <Stack direction="row" spacing={1} sx={{ mr: 2 }}>
                                    <Chip 
                                        icon={<TimerIcon />} 
                                        label={sessionStats.duration}
                                        size="small"
                                        color={sessionStats.isNearExpiry ? "warning" : "success"}
                                    />
                                    <Chip 
                                        icon={<SpeedIcon />} 
                                        label={`${sessionStats.commands} cmds`}
                                        size="small"
                                        color="info"
                                    />
                                </Stack>
                            )}
                            
                            {/* Security Alerts */}
                            {securityAlerts.length > 0 && (
                                <Badge badgeContent={securityAlerts.length} color="error">
                                    <IconButton color="inherit" size="small">
                                        <ShieldIcon />
                                    </IconButton>
                                </Badge>
                            )}
                            
                            {/* Action Buttons */}
                            <IconButton 
                                color="inherit" 
                                onClick={() => setShowStats(!showStats)}
                                size="small"
                            >
                                <InfoIcon />
                            </IconButton>
                            <IconButton 
                                color="inherit" 
                                onClick={() => setShowSettings(true)}
                                size="small"
                            >
                                <SettingsIcon />
                            </IconButton>
                            <IconButton 
                                color="inherit" 
                                onClick={toggleFullscreen}
                                size="small"
                            >
                                <FullscreenIcon />
                            </IconButton>                    </Toolbar>
                </AppBar>
                
                <CardContent>
                    {/* Controls */}
                    <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12} md={4}>
                            <TextField
                                label="Shell (optional)"
                                value={shell}
                                onChange={(e) => setShell(e.target.value)}
                                size="small"
                                disabled={active || connecting || isLocked}
                                fullWidth
                                placeholder="e.g., /bin/bash, /bin/zsh"
                                InputProps={{
                                    startAdornment: <VpnKeyIcon sx={{ mr: 1, color: 'action.active' }} />
                                }}
                            />
                        </Grid>
                        
                        <Grid item xs={12} md={3}>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={runAsRoot}
                                        onChange={(e) => setRunAsRoot(e.target.checked)}
                                        disabled={active || connecting || isLocked}
                                        color="warning"
                                    />
                                }
                                label={
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <SecurityIcon color={runAsRoot ? "warning" : "action"} />
                                        Run as Root
                                    </Box>
                                }
                            />
                        </Grid>
                        
                        <Grid item xs={12} md={5}>
                            <Stack direction="row" spacing={1}>
                                {active ? (
                                    <Button
                                        variant="contained"
                                        color="error"
                                        onClick={closeSession}
                                        startIcon={<StopIcon />}
                                        fullWidth
                                        size="large"
                                    >
                                        End Session
                                    </Button>
                                ) : (
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        onClick={startSession}
                                        disabled={connecting || isLocked}
                                        startIcon={connecting ? <CircularProgress size={20} /> : <PlayArrowIcon />}
                                        fullWidth
                                        size="large"
                                    >
                                        {connecting ? 'Connecting...' : 'Start Terminal'}
                                    </Button>
                                )}
                                  {statusLog.length > 0 && (
                                    <Tooltip title="Export Logs">
                                        <IconButton onClick={exportLogs} color="primary">
                                            <DownloadIcon />
                                        </IconButton>
                                    </Tooltip>
                                )}
                                
                                {active && sessionId && (
                                    <Tooltip title="Test Terminal Connection">
                                        <IconButton onClick={testTerminalConnection} color="secondary">
                                            <SpeedIcon />
                                        </IconButton>
                                    </Tooltip>
                                )}
                            </Stack>
                        </Grid>
                    </Grid>
                    
                    {/* Lockout Warning */}
                    {isLocked && (
                        <Alert severity="error" sx={{ mt: 2 }}>
                            <Typography variant="body2">
                                Account temporarily locked due to failed authentication attempts. 
                                Try again in {Math.ceil((lockedUntil - Date.now()) / 1000)} seconds.
                            </Typography>
                        </Alert>
                    )}
                </CardContent>
            </Card>

            {/* Session Statistics */}
            <Collapse in={showStats && active}>
                <Card sx={{ mb: 2 }}>
                    <CardContent>
                        <Typography variant="h6" gutterBottom>Session Statistics</Typography>
                        <Grid container spacing={2}>
                            <Grid item xs={6} md={3}>
                                <Typography variant="body2" color="text.secondary">Duration</Typography>
                                <Typography variant="h6">{sessionStats?.duration || 'N/A'}</Typography>
                            </Grid>
                            <Grid item xs={6} md={3}>
                                <Typography variant="body2" color="text.secondary">Remaining</Typography>
                                <Typography variant="h6" color={sessionStats?.isNearExpiry ? 'warning.main' : 'inherit'}>
                                    {sessionStats?.remaining || 'N/A'}
                                </Typography>
                            </Grid>
                            <Grid item xs={6} md={3}>
                                <Typography variant="body2" color="text.secondary">Commands</Typography>
                                <Typography variant="h6">{sessionStats?.commands || 0}</Typography>
                            </Grid>
                            <Grid item xs={6} md={3}>
                                <Typography variant="body2" color="text.secondary">Terminal Size</Typography>
                                <Typography variant="h6">{terminalSize.cols}×{terminalSize.rows}</Typography>
                            </Grid>
                        </Grid>
                    </CardContent>
                </Card>
            </Collapse>

            {/* Terminal Container - Only show in normal mode */}
            <Card elevation={6} sx={{ mb: 2, overflow: 'hidden' }}>
                <Box
                    ref={terminalRef}
                    id="terminal-normal-container"
                    sx={{
                        width: '100%',
                        height: 500,
                        minHeight: 300,
                        backgroundColor: CONFIG.UI.TERMINAL_THEMES[theme].background,
                        cursor: 'text',
                        '&:focus-within': {
                            outline: '2px solid',
                            outlineColor: 'primary.main',
                            outlineOffset: '-2px'
                        }
                    }}
                    onClick={() => {
                        try {
                            if (xtermRef.current) {
                                xtermRef.current.focus();
                            }
                        } catch (err) {
                            console.error('Focus error:', err);
                        }
                    }}
                />
            </Card>

            {/* Status Log */}
            <Card>
                <CardContent>
                    <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <HistoryIcon />
                        Activity Log
                        {statusLog.length > 0 && (
                            <Chip label={statusLog.length} size="small" color="primary" />
                        )}
                    </Typography>
                    
                    <Box
                        id="status-log"
                        sx={{
                            maxHeight: 200,
                            overflowY: 'auto',
                            backgroundColor: '#1e1e1e', // Darker background for better contrast
                            borderRadius: 1,
                            p: 1,
                            fontFamily: 'monospace',
                            fontSize: '0.8rem',
                            border: '1px solid #333' // Add subtle border
                        }}
                    >
                        {statusLog.length === 0 ? (
                            <Typography variant="body2" sx={{ color: '#888' }}>
                                Activity will appear here...
                            </Typography>
                        ) : (
                            statusLog.map((entry) => (
                                <Box key={entry.id} sx={{ 
                                    display: 'flex', 
                                    alignItems: 'center',
                                    gap: 1,
                                    py: 0.5,
                                    borderBottom: '1px solid #333',
                                    borderColor: 'rgba(255,255,255,0.1)'
                                }}>
                                    {entry.isSecurityEvent && <ShieldIcon sx={{ fontSize: 16, color: '#ffa726' }} />}
                                    <Typography 
                                        variant="caption" 
                                        sx={{ 
                                            color: entry.isError ? '#ff5252' : '#e0e0e0', // Light text for better contrast
                                            fontWeight: entry.isSecurityEvent ? 'bold' : 'normal',
                                            fontFamily: 'monospace'
                                        }}
                                    >
                                        [{entry.ts.toLocaleTimeString()}] {entry.msg}
                                    </Typography>
                                </Box>
                            ))
                        )}   
                    </Box>
                </CardContent>
            </Card>

            {/* Error Display */}
            {error && (
                <Alert severity="error" sx={{ mt: 2 }} onClose={() => setError('')}>
                    {error}
                </Alert>
            )}

            {/* Connection Status */}
            {connecting && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 2 }}>
                    <CircularProgress size={20} />
                    <Typography>Establishing secure connection...</Typography>
                </Box>
            )}

            {/* Dialogs */}
            <SecurityWarningDialog />
            <SettingsDialog />

            {/* Snackbar */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
            >
                <Alert 
                    onClose={() => setSnackbar(prev => ({ ...prev, open: false }))} 
                    severity={snackbar.severity}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
        </>
    );
}
